import React, { memo } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  Stack,
  Typography,
  Icon,
  Card,
  CardContent,
  Box,
} from "investira.react.components";
import { BaseTemplate, PaginationTemplate } from "../../components/templates";
import { OUTPUT } from "../../enums";
import useAcesso from "../../hooks/useAcesso";

const Reports = memo((props) => {
  const location = useLocation();
  const { canAcessar } = useAcesso();

  const REPORTS = {
    duration: {
      ...OUTPUT.duration,
      iconName: "report",
      available: canAcessar(OUTPUT.duration.output_id),
      descricao_longa:
        "Análise da sensibilidade do preço de títulos de renda fixa em relação a mudanças nas taxas de juros. Calcule a duration modificada e convexidade para gerenciar o risco de taxa de juros da sua carteira.",
    },
    enquadramento: {
      ...OUTPUT.enquadramento,
      iconName: "report",
      available: canAcessar(OUTPUT.enquadramento.output_id),
      descricao_longa:
        "Verifique se sua carteira está adequada aos limites regulatórios e políticas de investimento. Monitore a exposição por emissor, modalidade, prazo e outros critérios definidos na legislação e mandato.",
    },
    var: {
      ...OUTPUT.var,
      iconName: "report",
      available: canAcessar(OUTPUT.var.output_id),
      descricao_longa: `
      A avaliação da posição a partir de métricas de risco e desempenho.
      VaR (Value at Risk): Perda máxima potencial em um horizonte de tempo específico, com determinado nível de confiança.
      Índice de Sharpe: Retorno excedente obtido por unidade de risco, ajustado pela volatilidade.
      `,
    },
    stresstest: {
      ...OUTPUT.stresstest,
      iconName: "report",
      available: true,
      descricao_longa:
        "Simule o impacto de cenários extremos de mercado na sua carteira. Avalie como choques em taxas de juros, câmbio, bolsa e outros fatores de risco afetariam o valor dos ativos.",
    },
    conferenciacustodiante: {
      ...OUTPUT.conferenciacustodiante,
      iconName: "report",
      available: canAcessar(OUTPUT.conferenciacustodiante.output_id),
      descricao_longa:
        "Compare a posição da sua carteira no sistema com a posição do custodiante para identificar possíveis divergências.",
    },
    rentabilidade: {
      ...OUTPUT.rentabilidade,
      iconName: "report",
      available: false,
      descricao_longa:
        "Acompanhe o desempenho histórico da carteira com métricas de retorno absoluto e relativo. Compare com benchmarks, analise atribuição de performance e calcule indicadores de risco-retorno como Sharpe e Sortino.",
    },
    raiox: {
      ...OUTPUT.raiox,
      iconName: "report",
      available: false,
      descricao_longa:
        "Análise detalhada da composição da carteira, incluindo alocação por classe de ativo, setor, duration, rating e outros fatores. Visualize graficamente a distribuição dos investimentos e identifique concentrações.",
    },
    backtesting: {
      ...OUTPUT.backtesting,
      iconName: "report",
      available: false,
      descricao_longa:
        "Teste a eficácia dos modelos de risco aplicando-os a dados históricos. Verifique se as estimativas de VaR e outras métricas foram adequadas para prever perdas passadas.",
    },
    fronteiraeficiente: {
      ...OUTPUT.fronteiraeficiente,
      iconName: "report",
      available: false,
      descricao_longa:
        "Otimização de carteiras baseada na teoria de Markowitz. Encontre combinações de ativos que maximizam o retorno esperado para cada nível de risco, construindo portfólios na fronteira eficiente.",
    },
  };

  return (
    <BaseTemplate
      title={"Relatórios"}
      subtitle={props.subtitle}
      description=""
      maxWidth={"xl"}
    >
      <Box
        role="list"
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(1, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
          },
          gap: "16px 16px",
        }}
      >
        {Object.values(REPORTS).map((option) => (
          <Link
            to={option.available ? `${location.pathname}/${option.id}` : "#"}
            aria-label={`Ver lista de ${option.descricao}`}
            style={{ pointerEvents: option.available ? "auto" : "none" }}
          >
            <Card
              sx={{
                height: "100%",
                display: "flex",
                opacity: option.available ? 1 : 0.3,
              }}
            >
              <CardContent
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  flexGrow: 1,
                  p: 3,
                }}
              >
                <Stack gap="16px">
                  <Icon iconName={option.iconName} color="primary" size={32} />
                  <Stack>
                    <Typography variant="h6" component="h2" color="textPrimary">
                      {option.descricao}
                    </Typography>
                    <Typography
                      variant="caption"
                      component="h2"
                      color="textPrimary"
                      gutterBottom
                    >
                      {option.descricao_longa}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Link>
        ))}
      </Box>
    </BaseTemplate>
  );
});

PaginationTemplate.displayName = "PaginationTemplate";

export default Reports;
